import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import Search from "@/design/components/Search";
import FormGroup from "@/design/forms/FormGroup";
import InputToggle from "@/design/forms/InputToggle";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { viPlus, viXmark } from "@sippy-platform/valkyrie";

import {
  Alert,
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import AddPhonebookItem from "./AddPhonebookItem";
import PhonebookItem from "./PhonebookItem";

function Phonebooks({ formHandler, dispatch, ...props }) {
  useEffect(() => {
    Promise.resolve(Api.getPhonebooks());
  }, []);

  const phonebookStore = useSelector((state) => state.domain.phonebooks);

  // Phonebook drawer
  const [drawer, setDrawer] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const searchResults = useSearch(phonebookStore.list, searchValue, ["name"]);

  const closeDrawer = () => {
    setDrawer(undefined);
    setSearchValue("");
  };

  return (
    <FormGroup
      title="Phonebooks"
      actions={
        <Button onClick={() => setDrawer(true)} variant="contained" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      }
    >
      <Grid size={12}>
        <InputToggle
          id="showInternalPhonebook"
          onChange={formHandler}
          states={[false, true]}
          value={props.user.showInternalPhonebook}
          primary="Internal phonebook"
          secondary="Provide the internal phonebook to devices with this user."
        />
      </Grid>
      <Grid size={12}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
            gap: 1,
          }}
        >
          {props.user.phonebooks.length ? (
            props.user.phonebooks.map((phonebook) => (
              <PhonebookItem key={phonebook.id} phonebook={phonebook} user={props.user} dispatch={dispatch} />
            ))
          ) : (
            <Typography sx={{ gridColumn: "1 / -1" }}>This user has no phonebooks linked.</Typography>
          )}
        </Box>
      </Grid>
      <Drawer
        open={!!drawer}
        onClose={() => closeDrawer()}
        anchor="right"
        slotProps={{
          paper: {
            sx: (theme) => ({ maxWidth: 320, width: 1, p: 1.5, display: "flex", gap: 1, ...theme.scrollbar }),
            elevation: 4,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">Add phonebook</Typography>
          <IconButton onClick={() => closeDrawer()} color="inherit">
            <Valkyrie icon={viXmark} />
          </IconButton>
        </Stack>
        <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
          <Search blur value={searchValue} onChange={setSearchValue} />
        </Box>
        {props.user.phonebooks.length >= 4 && (
          <Alert severity="info">You have reached the limit of 4 phonebooks.</Alert>
        )}
        <List>
          {searchResults.length >= 1 ? (
            searchResults.map((phonebook) => (
              <AddPhonebookItem key={phonebook.id} phonebook={phonebook} dispatch={dispatch} user={props.user} />
            ))
          ) : (
            <ListItem>
              <ListItemText primary="There are no phonebooks available..." />
            </ListItem>
          )}
        </List>
      </Drawer>
    </FormGroup>
  );
}

Phonebooks.propTypes = {
  user: PropTypes.object,
  formHandler: PropTypes.func,
  dispatch: PropTypes.func,
};

export default Phonebooks;
